<script setup lang="ts">
import { useDisplay } from 'vuetify'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import SearchableSelectList, { type ISearchableSelectItem } from '@/components/createdUi/SearchableSelectList.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import { useDialogDismissGuard } from '@/composables/useDialogDismissGuard'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useStaffSettingsStore } from '../../stores/staffSettingsStore'

const props = defineProps<{
  modelValue: boolean
  mode: 'create' | 'edit'
  positionId?: string | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  saved: []
}>()

const { smAndDown } = useDisplay()
const staffSettingsStore = useStaffSettingsStore()
const feedbackStore = useFeedbackStore()

const formData = ref({
  name: '',
  description: '',
  superposition_id: null as string | null,
})

const showSuperpositionMenu = ref(false)
const initialSnapshot = ref('')
const showUnsavedChangesDialog = ref(false)
const errors = ref<{ name?: string }>({})

const isEditMode = computed(() => props.mode === 'edit' && Boolean(props.positionId))
const title = computed(() => isEditMode.value ? 'Редактирование должности' : 'Создание должности')

function getSnapshot() {
  return JSON.stringify({
    name: formData.value.name.trim(),
    description: formData.value.description.trim(),
    superposition_id: formData.value.superposition_id,
  })
}

function hasChanges() {
  return getSnapshot() !== initialSnapshot.value
}

const superpositionSelectItems = computed<ISearchableSelectItem[]>(() => {
  const all = staffSettingsStore.allPositionsSimple || []
  return all
    .filter(item => !props.positionId || String(item.id) !== String(props.positionId))
    .map(item => ({ id: item.id, label: item.name }))
})

const selectedSuperpositionName = computed(() => {
  if (!formData.value.superposition_id) return ''
  const item = superpositionSelectItems.value.find(position => String(position.id) === String(formData.value.superposition_id))
  return item ? item.label : ''
})

function onSelectSuperposition(item: ISearchableSelectItem) {
  formData.value.superposition_id = String(item.id)
  showSuperpositionMenu.value = false
}

function onClearSuperposition() {
  formData.value.superposition_id = null
}

watch(
  () => [props.modelValue, props.positionId] as const,
  async ([isOpen, id]) => {
    errors.value = {}
    showSuperpositionMenu.value = false
    if (!isOpen) return
    staffSettingsStore.fetchAllPositionsSimple()
    if (props.mode === 'edit' && id) {
      const detail = await staffSettingsStore.fetchPositionDetail(String(id))
      if (detail) {
        formData.value = {
          name: detail.name || '',
          description: detail.description || '',
          superposition_id: detail.superposition?.id ? String(detail.superposition.id) : null,
        }
      }
    } else {
      formData.value = {
        name: '',
        description: '',
        superposition_id: null,
      }
    }
    initialSnapshot.value = getSnapshot()
  },
)

function closeModalDirectly() {
  emit('update:modelValue', false)
  errors.value = {}
  showSuperpositionMenu.value = false
}

function handleCloseModal() {
  if (hasChanges())
    showUnsavedChangesDialog.value = true
  else
    closeModalDirectly()
}

const onDialogDismissAttempt = useDialogDismissGuard(handleCloseModal)

function handleCloseConfirm(confirmed: boolean) {
  showUnsavedChangesDialog.value = false
  if (confirmed)
    closeModalDirectly()
}

function validate() {
  errors.value = {}
  if (!formData.value.name.trim()) {
    errors.value.name = 'Наименование должности обязательно для заполнения'
    return false
  }
  if (formData.value.name.trim().length > 128) {
    errors.value.name = 'Наименование должности не должно превышать 128 символов'
    return false
  }
  return true
}

async function handleSave() {
  if (!validate()) return
  const payload = {
    name: formData.value.name.trim(),
    description: formData.value.description.trim(),
    superposition_id: formData.value.superposition_id || null,
  }
  try {
    if (isEditMode.value && props.positionId)
      await staffSettingsStore.updatePosition(props.positionId, payload)
    else
      await staffSettingsStore.createPosition(payload)
    emit('saved')
    closeModalDirectly()
  } catch {
    // Ошибка уже показана стором
  }
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="600"
    persistent
    @click:outside="onDialogDismissAttempt"
    @keydown.esc.stop="onDialogDismissAttempt"
  >
    <VCard>
      <div class="d-flex align-center justify-space-between pa-6">
        <span class="text-h4 staff-modal-title">{{ title }}</span>
        <CustomCloseButton :close-action="onDialogDismissAttempt" />
      </div>

      <VCardText>
        <div class="d-flex flex-column gap-5 px-0">
          <VTextField
            v-model="formData.name"
            placeholder="Введите наименование должности"
            variant="outlined"
            clearable
            persistent-placeholder
            :error="!!errors.name"
            :error-messages="errors.name"
            @click:clear="formData.name = ''"
          >
            <template #label>
              <span>Наименование должности<sup class="required-label">*</sup></span>
            </template>
          </VTextField>

          <VMenu
            v-model="showSuperpositionMenu"
            :close-on-content-click="false"
            :max-height="350"
            location="bottom"
            transition="scale-transition"
            content-class="entity-form-menu tree-select-menu"
          >
            <template #activator="{ props: menuProps }">
              <VTextField
                :model-value="selectedSuperpositionName"
                class="invisible-readonly cursor-pointer"
                :append-inner-icon="showSuperpositionMenu ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
                variant="outlined"
                density="comfortable"
                placeholder="Выберите вышестоящую должность"
                persistent-placeholder
                readonly
                clearable
                v-bind="menuProps"
                @click:append-inner.stop="showSuperpositionMenu = !showSuperpositionMenu"
                @click:clear="onClearSuperposition"
              >
                <template #label>
                  <span>Вышестоящая должность</span>
                </template>
              </VTextField>
            </template>

            <SearchableSelectList
              :items="superpositionSelectItems"
              placeholder="Поиск должности..."
              @select="onSelectSuperposition"
            />
          </VMenu>

          <VTextarea
            v-model="formData.description"
            placeholder="Краткое описание функций и должностных обязанностей"
            variant="outlined"
            persistent-placeholder
            rows="3"
            auto-grow
          >
            <template #label>
              <span>Описание обязанностей</span>
            </template>
          </VTextarea>

          <div class="d-flex align-center" :class="smAndDown ? 'flex-column' : 'justify-end'">
            <VBtn
              :block="smAndDown"
              color="primary"
              :loading="feedbackStore.isGlobalLoading"
              @click="handleSave"
            >
              {{ isEditMode ? 'Сохранить' : 'Создать' }}
            </VBtn>
          </div>
        </div>
      </VCardText>
    </VCard>

    <ConfirmDialog
      v-model:is-dialog-visible="showUnsavedChangesDialog"
      confirmation-question="У вас есть несохраненные изменения. Вы уверены, что хотите закрыть окно?"
      confirm-btn="Закрыть"
      reject-btn="Остаться"
      @confirm="handleCloseConfirm"
    />
  </VDialog>
</template>

<style scoped lang="scss">
.staff-modal-title {
  color: rgba(var(--v-theme-text-primary), var(--v-high-emphasis-opacity));
}

:global(.tree-select-menu) {
  max-width: 0 !important;
}
</style>
